import React, { useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

import { Divider, Col, Row, Input, Form, Space, Button, DatePicker, Tooltip, InputNumber, TimePicker, Select } from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { RollbackOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { HookAPI } from 'antd/es/modal/useModal';
import { NotificationInstance } from 'antd/es/notification/interface';

import UploadDocument from '@components/upload-document/upload-document';
import CargoCarrier from '@components/cargo-carrier/cargo-carrier';

import { toDayjsLocal, toDayjsUtc } from '@extensions/utils';

import { useAppSelector } from '@store/hooks';
import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';
import { IInboundCarrier } from '@src/entities/inbound-carrier';
import { ICountry } from '@entities/country';

import { InboundCarrierStatus, enumLabel } from '@src/enums/inbound-carrier-status';
import { Permission, hasPermission } from '@src/enums/permission';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { DocumentReferenceType } from '@enums/document-reference-type';
import { DocumentType } from '@enums/document-type';
import { CargoCarrierType } from '@enums/cargo-carrier-type';
import { IEnumItem } from '@enums/enum-item';
import { publicDecrypt } from 'crypto';

dayjs.extend(utc);
dayjs.extend(tz);

interface IInboundCarrierProps {
    carrier: IInboundCarrier;
    countries: Array<ICountry>;
    canManageCarrier: boolean;
    cargoCarrierTypes: Array<IEnumItem>;
    modal: HookAPI;
    loading: boolean;
    api: NotificationInstance;
    onFinish: () => void;
    onChange: (e: IInboundCarrier) => void;
    onRevertOnway: () => void;
    onClose: () => void;
}
const InboundCarrierWeb = (props: IInboundCarrierProps) => {
    const [form] = Form.useForm();

    const { TextArea } = Input;

    const { carrier, canManageCarrier, cargoCarrierTypes, modal, countries, loading, api, onChange, onFinish, onRevertOnway, onClose } =
        props;

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    useEffect(() => {
        if (carrier?.etdOn) {
            form.setFieldValue('etdDate', dayjs(toDayjsLocal(carrier.etdOn)).set('hour', 0).set('minute', 0).set('second', 0));
            form.setFieldValue('etdTime', dayjs(toDayjsLocal(carrier.etdOn)));
        }

        if (carrier?.etaOn) {
            form.setFieldValue('etaDate', dayjs(toDayjsLocal(carrier.etaOn)).set('hour', 0).set('minute', 0).set('second', 0));
            form.setFieldValue('etaTime', dayjs(toDayjsLocal(carrier.etaOn)));
        }
    }, []);

    useEffect(() => {
        console.log('ETD', carrier.etdOn);
    }, [carrier]);

    const onSave = () => {
        carrier.etdOn = toDayjsUtc(carrier.etdOn);
        carrier.etaOn = toDayjsUtc(carrier.etaOn);

        onFinish();
    };

    return (
        <Row>
            <Col span={10}>
                <Form colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} form={form} onFinish={onSave}>
                    <Form.Item
                        initialValue={carrier.number}
                        required={true}
                        label='Номер'
                        name='number'
                        rules={[{ required: true, message: 'Укажите номер рейса' }]}
                        wrapperCol={{ span: 8 }}
                    >
                        <Input
                            disabled={!canManageCarrier}
                            autoFocus
                            onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                                onChange({ ...carrier, number: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        initialValue={carrier.departurePoint}
                        required={true}
                        label='Пункт отправления'
                        name='departurePoint'
                        rules={[{ required: true, message: 'Укажите пункт отправления' }]}
                        wrapperCol={{ span: 8 }}
                    >
                        <Select
                            size='middle'
                            allowClear
                            onChange={(value) => onChange({ ...carrier, departurePoint: value })}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={countries.map((c) => {
                                return { value: c.airportCode, label: `${c.airportCode} / ${c.name}` };
                            })}
                        />
                    </Form.Item>

                    <Form.Item
                        initialValue={carrier.cargoCarrierType}
                        required={true}
                        label='Тип перевозчика'
                        name='сargoCarrierType'
                        rules={[{ required: true, message: 'Укажите тип перевозчика' }]}
                        wrapperCol={{ span: 8 }}
                    >
                        <Select
                            size='middle'
                            allowClear
                            onChange={(value) => onChange({ ...carrier, cargoCarrierType: value, cargoCarrierName: undefined })}
                            options={cargoCarrierTypes.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />
                    </Form.Item>

                    <Form.Item label='Перевозчик'>
                        <CargoCarrier
                            defaultCargoCarrier={carrier.cargoCarrierName}
                            type={carrier.cargoCarrierType}
                            canManage={canManageCarrier && !!carrier.cargoCarrierType}
                            api={api}
                            onSelect={(value: string | undefined) => {
                                onChange({ ...carrier, cargoCarrierName: value });
                            }}
                            serverFetch={serverFetch}
                        />
                    </Form.Item>

                    {carrier.id && (
                        <Form.Item label='Статус' wrapperCol={{ span: 8 }}>
                            <Space.Compact style={{ width: '100%' }}>
                                <Input disabled={true} defaultValue={enumLabel(carrier.status)} />
                                {hasPermission(userSession.permissions, Permission.FullAccess) &&
                                    carrier.status === InboundCarrierStatus.Finished && (
                                        <Tooltip title='Вернуть статус "В Пути"'>
                                            <Button
                                                type='primary'
                                                icon={<RollbackOutlined />}
                                                onClick={() => {
                                                    modal.confirm({
                                                        title: `Изменить статус машины на "В Пути"?`,
                                                        okType: 'primary',
                                                        icon: <QuestionCircleOutlined />,
                                                        okText: 'ОК',
                                                        cancelText: 'Отмена',
                                                        onOk: () => {
                                                            onRevertOnway();
                                                        },
                                                    });
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                            </Space.Compact>
                        </Form.Item>
                    )}

                    <Form.Item label='ETD'>
                        <Form.Item noStyle name='etdDate'>
                            <DatePicker
                                disabled={!canManageCarrier}
                                format={'DD.MM.YYYY'}
                                onChange={(data) => {
                                    let etdOn = dayjs(carrier?.etdOn);
                                    onChange({
                                        ...carrier,
                                        etdOn: dayjs(data)
                                            .set('hour', etdOn.hour() ?? 0)
                                            .set('minute', etdOn.minute() ?? 0)
                                            .set('second', 0),
                                    });
                                }}
                            />
                        </Form.Item>
                        <Form.Item noStyle name='etdTime'>
                            <TimePicker
                                disabled={!canManageCarrier}
                                format={'HH:mm'}
                                needConfirm={false}
                                showNow={false}
                                onChange={(data) => {
                                    let etdOn = !data
                                        ? dayjs(carrier.etdOn).set('hour', 0).set('minute', 0).set('second', 0)
                                        : dayjs(carrier.etdOn).set('hour', data.hour()).set('minute', data.minute()).set('second', 0);

                                    onChange({
                                        ...carrier,
                                        etdOn: etdOn,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Form.Item>
                    <Form.Item label='ETA'>
                        <Form.Item noStyle name='etaDate'>
                            <DatePicker
                                disabled={!canManageCarrier}
                                format={'DD.MM.YYYY'}
                                onChange={(data) => {
                                    let etaOn = dayjs(carrier?.etaOn);
                                    onChange({
                                        ...carrier,
                                        etaOn: dayjs(data)
                                            .set('hour', etaOn.hour() ?? 0)
                                            .set('minute', etaOn.minute() ?? 0)
                                            .set('second', 0),
                                    });
                                }}
                            />
                        </Form.Item>
                        <Form.Item noStyle name='etaTime'>
                            <TimePicker
                                disabled={!canManageCarrier}
                                format={'HH:mm'}
                                needConfirm={false}
                                showNow={false}
                                onChange={(data) => {
                                    let etaOn = !data
                                        ? dayjs(carrier.etaOn).set('hour', 0).set('minute', 0).set('second', 0)
                                        : dayjs(carrier.etaOn).set('hour', data.hour()).set('minute', data.minute()).set('second', 0);

                                    onChange({
                                        ...carrier,
                                        etaOn: etaOn,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Form.Item>
                    <Form.Item label='Брутто' name='grossWeight' initialValue={carrier.grossWeight}>
                        <Space.Compact>
                            <InputNumber
                                decimalSeparator=','
                                min={0}
                                disabled={!canManageCarrier}
                                stringMode
                                onChange={(value: number | null) => {
                                    onChange({ ...carrier, grossWeight: value });
                                }}
                            />
                            <Space.Addon>кг</Space.Addon>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item label='Комментарий' name='comment' initialValue={carrier.comment}>
                        <TextArea
                            disabled={!canManageCarrier && !hasPermission(userSession.permissions, Permission.SetCommentInboundCarrier)}
                            rows={4}
                            onChange={(data) => {
                                onChange({ ...carrier, comment: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 8, span: 16, xs: 16 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            {
                                <Button type='text' onClick={() => onClose()}>
                                    Закрыть
                                </Button>
                            }

                            {(canManageCarrier || hasPermission(userSession.permissions, Permission.SetCommentInboundCarrier)) && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>

                {carrier.id && (
                    <>
                        <Divider titlePlacement='left' style={{ marginTop: 40 }}>
                            Документы для клиентов
                        </Divider>
                        <Form.Item
                            wrapperCol={{ span: 24 }}
                            style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                        >
                            <UploadDocument
                                documentType={DocumentType.Other}
                                referenceId={carrier.id}
                                referenceType={DocumentReferenceType.Truck}
                                files={carrier.documents}
                                api={api}
                                onChange={(fileList: Array<UploadFile>) => {
                                    if (carrier) {
                                        onChange({ ...carrier, documents: fileList });
                                    }
                                }}
                                serverFetch={serverFetch}
                            />
                        </Form.Item>

                        <Divider titlePlacement='left' style={{ marginTop: 40, backgroundColor: 'var(--main-red)' }}>
                            Документы для внутреннего использования
                        </Divider>
                        <Form.Item
                            wrapperCol={{ span: 24 }}
                            style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                        >
                            <UploadDocument
                                documentType={DocumentType.InternalUseOnly}
                                referenceId={carrier.id}
                                referenceType={DocumentReferenceType.Truck}
                                files={carrier.internalDocuments}
                                api={api}
                                onChange={(fileList: Array<UploadFile>) => {
                                    if (carrier) {
                                        onChange({ ...carrier, internalDocuments: fileList });
                                    }
                                }}
                                serverFetch={serverFetch}
                            />
                        </Form.Item>
                    </>
                )}
            </Col>
        </Row>
    );
};

export default InboundCarrierWeb;
